import React, { useState, useEffect } from 'react';
import { useStyles } from '@webapps/styles';
import { Button, CircularProgress } from '@material-ui/core';
import { Share } from '@material-ui/icons';
import { Modal, useAuth, useMutation, useQuery, useToast } from '@webapps/reactjs-tech-components';
import NewModal from './NewModal';
import { FormikAutocompletePx, FormikTextField } from '@webapps/reactjs-tech-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addCourse, createRoom, createTeacher, deleteCourse, deleteRoom, deleteTeacher, editCourse, getRooms, getTeachers } from '@webapps/business';
import { formattedTime } from '../../utils/services';
import DropdownCrud from 'components/DropdownCrud';
// import DropdownCrud from '../DropdownCrud';

export default function AddCourseInPlannerModal({
  open,
  onCancel,
  courseToAdd,
  periodByDate,
  refetch,
  date,
  classroomId
}) {
  const { classes, cx, css } = useStyles(styles);
  const [keywordRoom, setKeywordRoom] = useState('');
  const [keywordTeacher, setKeywordTeacher] = useState('');
  const [deletingRoomId, setDeletingRoomId] = useState(null);
  const [deletingTeacherId, setDeletingTeacherId] = useState(null);
  const { addToast } = useToast();
  const { idUser: schoolId } = useAuth();

  const {
    isLoading: isAddCourseLoading,
    isSuccess: isAddCourseSuccess,
    error: isAddCourseError,
    mutateAsync: mutateAddCourse,
  } = useMutation(addCourse);

  const {
    isLoading: isEditCourseLoading,
    isSuccess: isEditCourseSuccess,
    error: isEditCourseError,
    mutateAsync: mutateEditCourse,
  } = useMutation(editCourse);

  const {
    isLoading: isDeleteCourseLoading,
    isSuccess: isDeleteCourseSuccess,
    error: isDeleteCourseError,
    mutateAsync: mutateDeleteCourse,
  } = useMutation(deleteCourse);

  const {
    isLoading: isDeleteRoomLoading,
    isSuccess: isDeleteRoomSuccess,
    error: isDeleteRoomError,
    mutateAsync: mutateDeleteRoom,
  } = useMutation(deleteRoom);

  const {
    isLoading: isDeleteTeacherLoading,
    isSuccess: isDeleteTeacherSuccess,
    error: isDeleteTeacherError,
    mutateAsync: mutateDeleteTeacher,
  } = useMutation(deleteTeacher);

  const {
    isLoading: isCreateRoomLoading,
    isSuccess: isCreateRoomSuccess,
    error: isCreateRoomError,
    mutateAsync: mutateCreateRoom,
  } = useMutation(createRoom);

  const {
    isLoading: isCreateTeacherLoading,
    isSuccess: isCreateTeacherSuccess,
    error: isCreateTeacherError,
    mutateAsync: mutateCreateTeacher,
  } = useMutation(createTeacher);


  const handleDeleteRoom = async (id) => {
    setDeletingRoomId(id);
    try {
      await mutateDeleteRoom({ id });
      await refetchRooms();
      // Si la salle supprimée était sélectionnée, la désélectionner
      if (formik.values.room?.id === id) {
        formik.setFieldValue('room', null);
      }
      addToast({
        message: 'Salle supprimée avec succès',
        type: 'success',
      });
    } catch (error) {
      console.log("Erreur lors de la suppression de la salle:", error);
      addToast({
        message: 'Erreur lors de la suppression de la salle',
        type: 'error',
      });
    } finally {
      setDeletingRoomId(null);
    }
  }

  const handleDeleteTeacher = async (id) => {
    setDeletingTeacherId(id);
    try {
      await mutateDeleteTeacher({ id });
      await refetchTeachers();
      // Si le professeur supprimé était sélectionné, le désélectionner
      if (formik.values.teacher?.id === id) {
        formik.setFieldValue('teacher', null);
      }
      addToast({
        message: 'Professeur supprimé avec succès',
        type: 'success',
      });
    } catch (error) {
      console.log("Erreur lors de la suppression du professeur:", error);
      addToast({
        message: 'Erreur lors de la suppression du professeur',
        type: 'error',
      });
    } finally {
      setDeletingTeacherId(null);
    }
  }

  const handleCreateRoom = async (data) => {
    try {
      const result = await mutateCreateRoom({
        data: {
          label: data,
          school: {
            id: schoolId,
          },
        }
      });
      await refetchRooms();
      // Sélectionner automatiquement la salle créée
      if (result?.data) {
        formik.setFieldValue('room', result.data);
      }
    } catch (error) {
      console.log("Erreur lors de la création de la salle:", error);
    }
  }

  const handleCreateTeacher = async (data) => {
    try {
      const result = await mutateCreateTeacher({
        data: {
          fullName: data,
          school: {
            id: schoolId,
          },
        }
      });
      await refetchTeachers();
      // Sélectionner automatiquement le professeur créé
      if (result?.data) {
        formik.setFieldValue('teacher', result.data);
      }
    } catch (error) {
      console.log("Erreur lors de la création du professeur:", error);
    }
  }

  const formik = useFormik({
    initialValues: {
      title: courseToAdd?.title || '',
      room: courseToAdd?.room || null,
      teacher: courseToAdd?.teacher || null,
      startTime: courseToAdd?.startTime || '',
      endTime: courseToAdd?.endTime || '',
    },
    onSubmit: async (values) => {
      console.log("values:", values);

      const dateOfCourse = new Date(date).toISOString().split('T')[0];

      if (courseToAdd?.id) {
        try {
          await mutateEditCourse({
            data: {
              id: courseToAdd.id,
              ...values,
              startTime: formattedTime(values.startTime),
              endTime: formattedTime(values.endTime),
              date: dateOfCourse,
              periodId: periodByDate.id,
              teacherId: values.teacher.id,
              roomId: values.room.id,
            }
          });
        } catch (error) {
          console.log("Erreur lors de la modification du cours:", error);
        }
      } else {
        try {
          //classroomId in query ?classroomId=123
          const { data } = await mutateAddCourse({
            data: {
              ...values,
              startTime: formattedTime(values.startTime),
              endTime: formattedTime(values.endTime),
              date: dateOfCourse,
              period: { id: periodByDate.id },
              classroomId: classroomId
            }
          });
        } catch (error) {
          console.log("Erreur lors de l'ajout du cours:", error);
        }
      }
    }
  });

  useEffect(() => {
    if (isAddCourseSuccess) {
      addToast({
        message: 'Le cours a été ajouté avec succès',
        type: 'success',
      });
      onCancel();
      refetch.refetchCourses();
      refetch.refetchConfigurationStatus();
    }

    if (isAddCourseError) {
      addToast({
        status: "error",
        message: isAddCourseError?.response?.data?.description,
      });
    }
  }, [isAddCourseSuccess, isAddCourseError]);

  useEffect(() => {
    if (isEditCourseSuccess) {
      addToast({
        message: 'Le cours a été modifié avec succès',
        type: 'success',
      });
      onCancel();
      refetch.refetchCourses();
      refetch.refetchConfigurationStatus();
    }

    if (isEditCourseError) {
      addToast({
        status: "error",
        message: isEditCourseError?.response?.data?.description,
      });
    }
  }, [isEditCourseSuccess, isEditCourseError]);

  useEffect(() => {
    if (isDeleteCourseSuccess) {
      addToast({
        message: 'Le cours a été supprimé avec succès',
        type: 'success',
      });
      onCancel();
      refetch.refetchCourses();
      refetch.refetchConfigurationStatus();
    }

    if (isDeleteCourseError) {
      addToast({
        status: "error",
        message: isDeleteCourseError?.response?.data?.description,
      });
    }
  }, [isDeleteCourseSuccess, isDeleteCourseError]);

  // Fonction pour convertir le format "Xh00" en "HH:mm"
  const formatTimeForInput = (time) => {
    if (!time) return '';
    if (time.includes(':')) return time;
    const [hours, minutes] = time.split('h').map(Number);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (courseToAdd) {
      formik.setValues({
        title: courseToAdd.title || '',
        room: courseToAdd.room || null,
        teacher: courseToAdd.teacher || null,
        startTime: formatTimeForInput(courseToAdd.startTime) || '',
        endTime: formatTimeForInput(courseToAdd.endTime) || '',
      });
    }
  }, [courseToAdd]);

  const { data: rooms, isLoading: isLoadingRooms, refetch: refetchRooms } = useQuery(getRooms, {
    key: ['rooms'],
  });
  const { data: teachers, isLoading: isLoadingTeachers, refetch: refetchTeachers } = useQuery(getTeachers, {
    key: ['teachers'],
  });

  const onEnter = (e) => {
    if (e.key === 'Enter') {
      if (courseToAdd?.id) {
        !isEditCourseLoading && formik.handleSubmit();
      } else {
        !isAddCourseLoading && formik.handleSubmit();
      }
    }
  }

  if (isLoadingRooms || isLoadingTeachers) {
    return <div>Chargement des salles et des professeurs...</div>;
  }

  const actions = courseToAdd?.id
    ? [
      {
        label: 'Supprimer',
        onClick: () => mutateDeleteCourse({ id: courseToAdd.id }),
        disabled: isDeleteCourseLoading,
        loading: isDeleteCourseLoading,
        type: 'deleteButton',
      },
      {
        label: 'Modifier',
        onClick: formik.handleSubmit,
        disabled: isEditCourseLoading,
        loading: isEditCourseLoading,
        type: 'validateButton',
      },
    ]
    : [
      {
        label: 'Valider',
        onClick: formik.handleSubmit,
        disabled: isAddCourseLoading,
        loading: isAddCourseLoading,
        type: 'validateButton',
      },
    ];


  return (
    <NewModal open={open} onCancel={onCancel}
      image={{
        src: "https://framerusercontent.com/images/DOPh6i0F3gfgI3TzaQYkACOPw.png",
        srcSet: "https://framerusercontent.com/images/DOPh6i0F3gfgI3TzaQYkACOPw.png?scale-down-to=512 512w,https://framerusercontent.com/images/DOPh6i0F3gfgI3TzaQYkACOPw.png?scale-down-to=1024 1024w,https://framerusercontent.com/images/DOPh6i0F3gfgI3TzaQYkACOPw.png 1574w",
        sizes: "165.6842px",
        alt: "",
      }}
      title="Ajouter une période de l'emploi du temps"
      subtitle="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aliquam ac ante vel ante vulputate porttitor nec in eros."
      actions={actions}
    >
      <div className={classes.container}>
        <div className={classes.form}>
          <div className={classes.inputGroup}>
            <div className={classes.label}>Nom de la période (cours ou autre)</div>
            <FormikTextField
              formik={formik}
              fullWidth
              name="title"
              placeholder="Taper son nom"
              value={formik.values.title}
              className={classes.inputButton}
              onKeyDown={onEnter}
            />
          </div>
          <div className={classes.grid}>
            <div className={classes.inputGroup}>
              <div className={classes.label}>Nom de la salle</div>
              <DropdownCrud
                addButtonLabel='Nom de la salle'
                suggestions={rooms}
                handleCreate={handleCreateRoom}
                handleDelete={handleDeleteRoom}
                value={formik.values.room}
                onChange={(room) => formik.setFieldValue('room', room)}
                isCreateLoading={isCreateRoomLoading}
                isDeleteLoading={!!deletingRoomId}
                deletingItemId={deletingRoomId}
              />
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.label}>Professeur</div>
              <DropdownCrud
                addButtonLabel='Nom du professeur'
                suggestions={teachers}
                dataTransform={(data) => data?.fullName}
                handleCreate={handleCreateTeacher}
                handleDelete={handleDeleteTeacher}
                value={formik.values.teacher}
                onChange={(teacher) => formik.setFieldValue('teacher', teacher)}
                isCreateLoading={isCreateTeacherLoading}
                isDeleteLoading={!!deletingTeacherId}
                deletingItemId={deletingTeacherId}
              />
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.label}>Horaire de début</div>
              <FormikTextField
                formik={formik}
                onKeyDown={onEnter}
                fullWidth
                name="startTime"
                type="time"
                className={classes.inputButton}
              />
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.label}>Horaire de fin</div>
              <FormikTextField
                formik={formik}
                onKeyDown={onEnter}
                fullWidth
                name="endTime"
                type="time"
                className={classes.inputButton}
              />
            </div>
          </div>
          {/* {
            courseToAdd?.id ?
              <>
                <Button
                  className={classes.validateButton}
                  onClick={formik.handleSubmit}
                  disabled={isEditCourseLoading}
                >
                  {isEditCourseLoading ? <CircularProgress size={20} color='white' /> : <div className={classes.validateText}>Modifier</div>}
                </Button>
                <Button
                  className={cx(classes.validateButton, classes.deleteButton)}
                  onClick={() => mutateDeleteCourse({ id: courseToAdd.id })}
                  disabled={isDeleteCourseLoading}
                >
                  {isDeleteCourseLoading ? <CircularProgress size={20} color='white' /> : <div className={classes.validateText}>Supprimer</div>}
                </Button>
              </>
              :
              <Button
                className={classes.validateButton}
                onClick={formik.handleSubmit}
                disabled={isAddCourseLoading}
              >
                {isAddCourseLoading ? <CircularProgress size={20} color='white' /> : <div className={classes.validateText}>Valider</div>}
              </Button>

          } */}
        </div>
      </div>
    </NewModal >
  );
}


const styles = ({ spacing }) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: "20px",

    ".MuiInputBase-root": {
      borderRadius: "10px !important",
    }
  },
  header: {
    alignItems: "center",
    backgroundColor: "#f3f7ff",
    borderTopLeftRadius: "20px",
    borderTopRightRadius: "20px",
    display: "flex",
    flexDirection: "column",
    padding: "24px",
    width: "100%",
    gap: "30px",
    overflow: "visible",
  },
  logo: {
    aspectRatio: "2.02054 / 1",
    width: "166px",
    position: "relative",
    overflow: "visible",
  },
  headerText: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    width: "388px",
    gap: "10px",
    // overflow: "hidden",
  },
  title: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "24px",
    fontWeight: "600",
    lineHeight: "28px",
    textAlign: "center",
    color: "#152652",
    width: "100%",
  },
  description: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: "14px",
    lineHeight: "18px",
    textAlign: "center",
    color: "#5d75a8",
    width: "100%",
  },
  shareButton: {
    border: "1px solid #c0d9ff",
    backgroundColor: "#f3f7ff",
    borderRadius: "20px",
    display: "flex",
    height: "26px",
    width: "26px",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "visible",
    "&:hover": {
      borderColor: "#0060f9",
      "& $shareIcon": {
        fill: "#0060f9",
      },
    },
  },
  shareIcon: {
    height: "16px",
    width: "16px",
    fill: "#5d75a8",
  },
  form: {
    alignItems: "flex-start",
    backgroundColor: "#ffffff",
    borderBottomLeftRadius: "20px",
    borderBottomRightRadius: "20px",
    display: "flex",
    flexDirection: "column",
    // padding: "24px",
    width: "100%",
    gap: "20px",
    // overflow: "hidden",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
    position: "relative",
    // overflow: "hidden",
  },
  label: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: "14px",
    lineHeight: "15px",
    color: "#92abd2",
  },
  inputButton: {
    border: "1px solid #c0d9ff",
    backgroundColor: "#f3f7ff",
    borderRadius: "10px !important",
    display: "flex",
    height: "40px",
    justifyContent: "space-between",
    padding: "5px 15px",
    width: "100%",
    alignItems: "center",
    position: "relative",
    overflow: "visible",
    "&:hover": {
      borderColor: "#0060f9",
      "& $inputText": { color: "#0060f9" },
      "& $selectionText": { color: "#0060f9" },
      "& $iconRight": { fill: "#0060f9" },
    },
  },
  inputText: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "13.5px",
    fontWeight: "500",
    lineHeight: "23.625px",
    textAlign: "center",
    color: "#92abd2",
  },
  selectionText: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "13.5px",
    fontWeight: "500",
    lineHeight: "23.625px",
    textAlign: "center",
    color: "#5d75a8",
  },
  iconRight: {
    height: "24px",
    width: "24px",
    fill: "#5d75a8",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(50px, 1fr))",
    gridTemplateRows: "repeat(2, minmax(0px, 1fr))",
    gap: "20px",
    width: "100%",
    // overflow: "hidden",
  },
  validateButton: {
    backgroundColor: "#0060f9",
    borderRadius: "40px",
    display: "flex",
    height: "40px",
    padding: "5px 15px",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    overflow: "visible",
    "&:hover": {
      backgroundColor: "#004ac1",
    },
  },
  deleteButton: {
    backgroundColor: "#F50057",
    "&:hover": {
      backgroundColor: "#BD0042",
    },
  },

  validateText: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "13.5px",
    fontWeight: "500",
    lineHeight: "23.625px",
    textAlign: "center",
    color: "#ffffff",
  },
});
